import { numericFormatter as reactNumericFormatter } from 'react-number-format';

import { Cost, type CostWireFormat } from '../types/api/Cost/Cost';
import {
  Currency,
  CurrencyWireFormat,
  NewCurrencyFromWireFormat,
} from '../types/api/Currency/Currency';
import { hasStringValue } from './equality';
import { getCurrencySymbol } from './format';
import { logger } from './logger';
import { getUnitLabelWithSlash } from './unitHelpers';
import type { YupSchemaNullableCurrencyType } from './yupHelpers';

/**
 * CurrencyPrecision returns the number of decimal places to use when
 * displaying a number via precision. The precision returned appropriately
 * will appropriately remove padded ending zeroes.
 */
export const CurrencyPrecision = (cur: Currency): number => {
  const decimalDigits = cur.number.split('.')[1] ?? '';
  switch (decimalDigits.length) {
    case 0:
    case 1:
    case 2:
      return 2;
    case 3:
      if (decimalDigits[2] !== '0') {
        return 3;
      }
      return 2;
    // If any digit after the 3rd is not 0, return 4.
    // Otherwise follow the rule of case 3.
    default:
      if (
        decimalDigits
          .slice(3)
          .split('')
          .some((digit) => digit !== '0')
      ) {
        return 4;
      }
      if (decimalDigits[2] !== '0') {
        return 3;
      }
      return 2;
  }
};

export const CurrencyPrecisionFormat = (cur: Currency, precision: number | undefined): string => {
  let cleanPrecision = precision;
  if (cleanPrecision === undefined) {
    cleanPrecision = CurrencyPrecision(cur);
  }
  if (cleanPrecision < 0) {
    logger.warn('CurrencyString: precision must be >= 0. Setting to 0.');
    cleanPrecision = 0;
  }
  if (!Number.isInteger(cleanPrecision)) {
    logger.warn('CurrencyString: precision must be an integer. Rounding to integer.');
    cleanPrecision = Math.round(cleanPrecision);
  }
  return `0,0.${'0'.repeat(cleanPrecision)}`;
};

type CurrencyStringOpts = {
  cur: Currency;

  /** If true, prepend the currency symbol, else do not. Defaults to true. */
  includeSign?: boolean;

  /** Overwrites the default CurrencyPrecision behavior. */
  precision?: number;

  /**
   * Currency code used for the symbol (e.g. 'GBP' renders '£').
   * Defaults to '$' when omitted or empty.
   */
  currencyCode?: string | null;
};

/** Given a Currency, return a human readable locale string */
export const CurrencyString = ({
  cur,
  includeSign = true,
  precision,
  currencyCode,
}: CurrencyStringOpts): string => {
  // `cur` isn't always a real Currency instance in practice (e.g. plain wire-format
  // objects from callers that didn't go through NewCurrency), so normalize before
  // calling instance methods on it.
  const currency = cur instanceof Currency ? cur : new Currency(cur);
  const precisionFormat = CurrencyPrecisionFormat(currency, precision);
  if (!includeSign) {
    return currency.asDinero().toFormat(precisionFormat);
  }
  if (hasStringValue(currencyCode ?? '')) {
    return getCurrencySymbol(currencyCode) + currency.asDinero().toFormat(precisionFormat);
  }
  return currency.asDinero().toFormat(`$${precisionFormat}`);
};

type NullableCurrencyStringOpts = {
  cur: Currency | null;

  /** If true, prepend the currency symbol, else do not. Defaults to true. */
  includeSign?: boolean;
  /**
   * If true, empty string is returned. Else '0' or '$0' is returned.
   * Defaults to true.
   */
  omitNull?: boolean;

  /** Default 4. */
  precision?: number;

  /**
   * Currency code used for the symbol (e.g. 'GBP' renders '£').
   * Defaults to '$' when omitted or empty.
   */
  currencyCode?: string | null;
};

/** Given a Currency | null, return a human readable locale string */
export const NullableCurrencyString = ({
  cur,
  includeSign = true,
  omitNull = true,
  precision,
  currencyCode,
}: NullableCurrencyStringOpts): string => {
  if (cur !== null) {
    return CurrencyString({ cur, includeSign, precision, currencyCode });
  }
  return omitNull
    ? ''
    : CurrencyString({ cur: Currency.zero(), includeSign, precision, currencyCode });
};

type FormattedCurrencyOpts = {
  cur: CurrencyWireFormat;
};

/**
 * Given a `CurrencyWireFormat` from formik state,
 * format it such that the number is a human readable locale string.
 * By using reactNumericFormatter, it also allows the user to pass in
 * current-changed values that allow for incomplete currency amounts.
 */
export const FormattedYupCurrency = ({ cur }: FormattedCurrencyOpts): CurrencyWireFormat => {
  const curNumber = reactNumericFormatter(cur.number, {
    thousandSeparator: true,
    prefix: undefined,
  });
  return {
    number: curNumber,
    currency: cur.currency,
  };
};

type FormattedNullableYupCurrencyOpts = {
  cur: YupSchemaNullableCurrencyType;
};

/**
 * Given a `YupSchemaNullableCurrencyType` from formik state,
 * format it such that the number is a human readable locale string.
 */
export const FormattedNullableYupCurrency = ({
  cur,
}: FormattedNullableYupCurrencyOpts): YupSchemaNullableCurrencyType => {
  if (cur === null) {
    return null;
  }
  return FormattedYupCurrency({ cur });
};

type CostStringOpts = {
  cost: CostWireFormat;

  /** If true, prepend '$', else do not. Defaults to true. */
  includeSign?: boolean;

  precision?: number;

  currencyCode?: string | null;
};

/** Given a Cost, return a human readable locale string */
export const CostString = ({
  cost,
  includeSign = true,
  precision,
  currencyCode,
}: CostStringOpts): string => {
  const currencyString = CurrencyString({
    cur: NewCurrencyFromWireFormat(cost.amount),
    includeSign,
    precision,
    currencyCode,
  });
  const unitLabel = getUnitLabelWithSlash(cost.unit);
  return `${currencyString}${unitLabel}`;
};

type NullableCostStringOpts = {
  cost: Cost | null;

  /**
   * If true, prepend '$', else do not.
   * @default true
   */
  includeSign?: boolean;
  /** Overwrites the default CurrencyPrecision behavior. */
  precision?: number;
  /**
   * If true, empty string is returned. Else '0' or '$0' is returned.
   * @default true
   */
  omitNull?: boolean;
  /**
   * If defined, this is the string that is returned if cost is null.
   * @default undefined
   */
  nullPlaceholder?: string;

  currencyCode?: string | null;
};

/** Given a Cost | null, return a human readable locale string */
export const NullableCostString = ({
  cost,
  includeSign = true,
  omitNull = true,
  nullPlaceholder = undefined,
  precision,
  currencyCode,
}: NullableCostStringOpts): string => {
  if (cost !== null) {
    return CostString({ cost, includeSign, precision, currencyCode });
  }
  if (omitNull) {
    return '';
  }
  if (nullPlaceholder !== undefined) {
    return nullPlaceholder;
  }
  return CostString({
    cost: Cost.zero(),
    includeSign,
    precision,
    currencyCode,
  });
};

/**
 * Checks if a nullable currency value is empty.
 * Returns true if the currency is null or if the number field is empty/whitespace.
 */
export const isNullableCurrencyEmpty = (value: YupSchemaNullableCurrencyType): boolean =>
  value === null || !hasStringValue(value.number);

export const numericFormatter = reactNumericFormatter;
